// Savepoint-name slot + in-buffer savepoint inventory.
//
// `ROLLBACK TO ▮` and `RELEASE ▮` take the name of a savepoint the script
// declared earlier. Nothing in the schema can supply it — a savepoint is
// transaction state created by a statement in the very buffer being edited —
// so before this module the caret fell to `unknown` and answered with the
// table list (sqlite script.50 §9/§10).
//
// The inventory is BUFFER-WIDE up to the caret rather than statement-scoped,
// for the same reason MySQL user variables are (`findUserVariables`): a
// savepoint declared by an earlier statement is live for every later one in
// the same session, and the editor's buffer is that session's script.
//
// No lifecycle modelling. `RELEASE sp` and `ROLLBACK TO sp` both end a
// savepoint's usable life on most engines, but tracking that would mean
// deciding what an unexecuted or partially executed script "has open", and
// suppressing a name the user is deliberately about to re-declare is worse
// than offering one they will not use. Every declared name is offered.
//
// Cross-engine: all five dialects have savepoints, with two spellings.
//   pgsql / mysql / sqlite / oracle — `SAVEPOINT <name>` declares;
//     `ROLLBACK [WORK|TRANSACTION] TO [SAVEPOINT] <name>` references it, and
//     `RELEASE [SAVEPOINT] <name>` releases it (Oracle has no RELEASE — its
//     savepoints live until the transaction ends).
//   mssql — `SAVE TRANSACTION <name>` / `SAVE TRAN <name>` declares;
//     `ROLLBACK TRANSACTION <name>` / `ROLLBACK TRAN <name>` references it.
//     T-SQL has neither `TO` nor `RELEASE`.

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { tokenize, type Token } from './tokenizer';

/**
 * Claim the caret when it is a savepoint-name slot, emitting the in-buffer
 * inventory. Returns true when the slot was claimed so the caller
 * short-circuits — a name slot is CLOSED, so it is claimed even when the
 * buffer declares nothing: an empty list is the honest answer there, and the
 * relation flood it replaces was never a legal completion.
 */
export function emitSavepointNameCandidates(
  out: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  // `SAVEPOINT ▮` / mssql `SAVE TRANSACTION ▮` — the caret is NAMING a new
  // savepoint. Nothing can be suggested (the name is the user's to invent),
  // but the slot is just as CLOSED as the reference slots below, so claim it
  // and answer with an empty list rather than letting the caret fall to
  // `unknown` and flood the widget with the table list. Closes sqlite
  // script.50 §1/§6; the same statement exists on all five engines, so the
  // shared claim fixes every one of them at once.
  if (isAtSavepointDeclarationSlot(text, offset, statementStart, engine)) return true;
  if (!isAtSavepointReferenceSlot(text, offset, statementStart, engine)) return false;
  const names = findInBufferSavepointNames(text, offset, engine);
  names.forEach((name, index) => {
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'variable',
      tier: 1,
      detail: 'savepoint',
      // Declaration order, innermost (most recently declared) last. The
      // ranker would otherwise sort these alphabetically, which for a
      // `sp_outer` / `sp_inner` pair inverts the nesting the user sees.
      orderHint: index,
    });
  });
  return true;
}

/**
 * Every savepoint name declared in `text` before `offset`, in declaration
 * order, deduped case-insensitively (first spelling wins — that is the one
 * the user typed and the one their later reference will match).
 */
export function findInBufferSavepointNames(
  text: string,
  offset: number,
  engine: DatabaseEngineId,
): readonly string[] {
  const tokens = tokenize(text, 0, offset, engine);
  const seen = new Set<string>();
  const out: string[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const word = wordOf(tokens[i]);
    if (word === null) continue;
    let nameIdx = -1;
    if (engine === 'mssql') {
      // `SAVE TRANSACTION <name>` / `SAVE TRAN <name>`.
      if (word !== 'SAVE') continue;
      const next = wordOf(tokens[i + 1]);
      if (next !== 'TRANSACTION' && next !== 'TRAN') continue;
      nameIdx = i + 2;
    } else {
      if (word !== 'SAVEPOINT') continue;
      // `RELEASE SAVEPOINT x` / `ROLLBACK TO SAVEPOINT x` are REFERENCES, not
      // declarations — skip when the preceding word makes this one a noise
      // word rather than the declaring verb.
      const prev = wordOf(tokens[i - 1]);
      if (prev === 'RELEASE' || prev === 'TO') continue;
      nameIdx = i + 1;
    }
    const nameTok = tokens[nameIdx];
    if (!nameTok || nameTok.type !== 'ident') continue;
    // A name token that ends exactly at the caret is the one being typed, not
    // a completed declaration — offering it back would suggest the prefix to
    // itself.
    if (nameTok.end >= offset) continue;
    const name = nameTok.text;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/**
 * True when the caret sits where a NEW savepoint's name belongs — the
 * declaring statement (`SAVEPOINT ▮`, mssql `SAVE TRANSACTION ▮` /
 * `SAVE TRAN ▮`). Distinct from the reference slots: no inventory applies,
 * because the user is inventing a name that does not exist yet.
 */
export function isAtSavepointDeclarationSlot(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  const words = statementLeadWords(text, statementStart, offset, engine);
  if (engine === 'mssql') {
    return (
      words.length === 2 &&
      words[0] === 'SAVE' &&
      (words[1] === 'TRANSACTION' || words[1] === 'TRAN')
    );
  }
  return words.length === 1 && words[0] === 'SAVEPOINT';
}

/**
 * True when the caret sits where a savepoint NAME belongs. A partially typed
 * name still counts, so the list stays visible while the user types.
 */
export function isAtSavepointReferenceSlot(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  const words = statementLeadWords(text, statementStart, offset, engine);
  if (words.length === 0) return false;

  if (engine === 'mssql') {
    // `ROLLBACK TRANSACTION ▮` / `ROLLBACK TRAN ▮`. The bare `ROLLBACK ▮` form
    // is NOT a name slot — T-SQL requires the TRANSACTION/TRAN keyword before
    // a savepoint name.
    return (
      words.length === 2 &&
      words[0] === 'ROLLBACK' &&
      (words[1] === 'TRANSACTION' || words[1] === 'TRAN')
    );
  }

  const last = words[words.length - 1]!;
  // `RELEASE ▮` / `RELEASE SAVEPOINT ▮`. Oracle has no RELEASE statement.
  if (engine !== 'oracle' && words[0] === 'RELEASE') {
    return words.length === 1 || (words.length === 2 && last === 'SAVEPOINT');
  }
  // `ROLLBACK [WORK|TRANSACTION] TO [SAVEPOINT] ▮`.
  if (words[0] === 'ROLLBACK') {
    if (last === 'TO') return true;
    if (last === 'SAVEPOINT' && words[words.length - 2] === 'TO') return true;
  }
  return false;
}

/** The statement's leading keyword words up to the caret, with a partially
 *  typed trailing identifier dropped. Returns [] when anything that is not a
 *  bare word appears (a paren, an operator, a literal) — no savepoint
 *  statement contains one, so such a prefix is some other statement. */
function statementLeadWords(
  text: string,
  statementStart: number,
  offset: number,
  engine: DatabaseEngineId,
): readonly string[] {
  const tokens = tokenize(text, statementStart, offset, engine);
  if (tokens.length === 0) return [];
  let end = tokens.length;
  const lastTok = tokens[end - 1]!;
  if (lastTok.end >= offset && lastTok.type === 'ident') end -= 1;
  const words: string[] = [];
  for (let i = 0; i < end; i += 1) {
    const word = wordOf(tokens[i]);
    if (word === null) return [];
    words.push(word);
  }
  return words;
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
